import React, { useState, useMemo, useEffect, useRef } from "react";

import { ArrowBack, ChevronLeft, ChevronRight, Search, Schedule, School, FileDownload } from "@mui/icons-material";
import { Alert, Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import styles from "./SessionDashboard.module.scss";
import { apiClient } from "../../../api/client";
import {
  SessionDashboardChild,
  SessionDashboardEducator,
  ServiceBookingHttpResponse,
} from "../../../api/PartnerPortalGatewayApi";
import { useLoading } from "../../../contexts/LoadingContext";
import { useDownloadCsv, convertToCsv } from "../../../hooks";
import { useSessionDashboard, SessionType } from "../../../hooks/useSessionDashboard";
import { RootState } from "../../../store";
import { groupAndSortEducators } from "../../../utils/educatorGrouping";
import { formatTime } from "../../../utils/timeFunctions";
import { ChildCard, getChildStatus, getStatusLabel, IChildCardData, ChildStatus } from "../../common/ChildCard";
import { EducatorSubSection, toEmployeePosition } from "../../educators/EducatorSubSection";

// Re-export ChildStatus for backward compatibility
export type { ChildStatus } from "../../common/ChildCard";

const getFullName = (child: SessionDashboardChild): string => {
  return `${child.firstName ?? ""} ${child.lastName ?? ""}`.trim();
};

const formatSessionTime = (start: dayjs.Dayjs, end: dayjs.Dayjs): string => {
  return `${formatTime(start)} - ${formatTime(end)}`;
};

const getSessionTypeLabel = (sessionType: SessionType | null): string => {
  switch (sessionType) {
    case "previous":
      return "Previous Session";
    case "current":
      return "Current Session";
    case "upcoming":
      return "Upcoming Session";
    default:
      return "";
  }
};

export const SessionDashboard: React.FC = () => {
  const selectedService = useSelector((state: RootState) => state.service.selectedService);
  const { showLoading, hideLoading } = useLoading();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<ChildStatus | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [changedMetrics, setChangedMetrics] = useState<Set<string>>(new Set());
  const [changedChildIds, setChangedChildIds] = useState<Set<number>>(new Set());
  const [changedEducatorIds, setChangedEducatorIds] = useState<Set<number>>(new Set());
  const previousMetricsRef = useRef<{ [key: string]: number | SessionDashboardChild[] | SessionDashboardEducator[] }>(
    {},
  );

  const {
    sessions,
    selectedSession,
    selectedSessionType,
    dashboardData,
    isLoadingSessions,
    isLoadingDashboard,
    sessionsError,
    dashboardError,
    selectPreviousSession,
    selectNextSession,
    hasPreviousSession,
    hasNextSession,
  } = useSessionDashboard(selectedService?.serviceId || null);

  // Download CSV hook
  const { isDownloading, downloadCsv } = useDownloadCsv((payload: { bookings: ServiceBookingHttpResponse[] }) => {
    // Transform bookings to CSV format (same as BookingsPage)
    const csvData = (payload.bookings || []).map((booking) => ({
      "Session Name": booking.rollName || "",
      "Session Date": booking.bookingDate ? dayjs(booking.bookingDate).format("YYYY-MM-DD") : "",
      "Child Name": `${booking.childFirstName || ""} ${booking.childSurname || ""}`.trim(),
      "Parent Name": `${booking.parentFirstName || ""} ${booking.parentLastName || ""}`.trim(),
      "Sign In Time": booking.rollMarkingTimeIn?.format("HH:mm") || "",
      "Sign Out Time": booking.rollMarkingTimeOut?.format("HH:mm") || "",
      Excluded: booking.isExcluded ? "Yes" : "No",
    }));

    return {
      data: convertToCsv(csvData),
    };
  });

  // Handle CSV download - fetch all bookings for the selected session's date range, then filter to only include those for sessions displayed on the dashboard
  // Don't love this approach but it was the most efficient way to get all necessary booking data without making much bigger API changes to support fetching dashboard-specific booking data
  const handleDownloadCsv = async () => {
    if (!selectedService?.serviceId || !selectedSession || isDownloading) {
      return;
    }

    try {
      // Get all unique dates from the sessions array
      const uniqueDates = Array.from(new Set(sessions.map((session) => session.rollDate?.format("YYYY-MM-DD")))).filter(
        (date) => date !== undefined,
      ) as string[];

      // Fetch bookings for each unique date
      const allBookingsArrays = await Promise.all(
        uniqueDates.map((dateStr) => {
          const date = dayjs(dateStr);
          return apiClient.bookings.getBookingsByServiceId(
            selectedService.serviceId!,
            date,
            false, // includeExcluded = false
            true, // excludeFeeWaiverBookings = true
          );
        }),
      );

      // Flatten all bookings into a single array
      const allBookings = allBookingsArrays.flat();

      // Create a set of {date, rollId} combinations from the dashboard sessions
      const sessionDateRollCombos = new Set(sessions.map((s) => `${s.rollDate?.format("YYYY-MM-DD")}-${s.rollId}`));

      // Filter bookings to only include those for sessions displayed on the dashboard
      const filteredBookings = allBookings.filter((booking) => {
        const bookingDateStr = booking.bookingDate ? dayjs(booking.bookingDate).format("YYYY-MM-DD") : null;
        return sessionDateRollCombos.has(`${bookingDateStr}-${booking.rollId}`);
      });

      await downloadCsv({
        data: { bookings: filteredBookings },
        fileName: `Attendance for ${selectedService.serviceName} at ${dayjs().format("YYYY-MM-DD-HH-mm-ss")}.csv`,
      });
    } catch (error) {
      console.error("Failed to download CSV:", error);
    }
  };

  // Show global loading overlay when loading
  useEffect(() => {
    if (isLoadingSessions || isLoadingDashboard) {
      showLoading(isLoadingSessions ? "Loading sessions..." : "Loading dashboard...");
    } else {
      hideLoading();
    }
  }, [isLoadingSessions, isLoadingDashboard, showLoading, hideLoading]);

  // Update last updated time when dashboard data changes (including background refreshes)
  useEffect(() => {
    if (dashboardData) {
      setLastUpdated(new Date());
    }
  }, [dashboardData]);

  // Calculate metrics from all children (not filtered by search)
  const actualDashboardData = dashboardData;
  const allChildren = actualDashboardData?.children || [];
  const totalBookings = allChildren.length;
  const inSessionCount = allChildren.filter((c) => getChildStatus(c.eventType) === "inSession").length;
  const signedOutCount = allChildren.filter((c) => getChildStatus(c.eventType) === "signedOut").length;
  const atEcaCount = allChildren.filter((c) => getChildStatus(c.eventType) === "atEca").length;
  const returningCount = allChildren.filter((c) => getChildStatus(c.eventType) === "returning").length;
  const notArrivedCount = allChildren.filter((c) => getChildStatus(c.eventType) === "notArrived").length;
  const absentCount = allChildren.filter((c) => getChildStatus(c.eventType) === "absent").length;

  // Filter children by search query
  const filteredChildren = useMemo(() => {
    if (!actualDashboardData?.children) return [];
    if (!searchQuery.trim()) return actualDashboardData.children;

    const query = searchQuery.toLowerCase();
    return actualDashboardData.children.filter(
      (child) =>
        getFullName(child).toLowerCase().includes(query) ||
        child.firstName?.toLowerCase().includes(query) ||
        child.lastName?.toLowerCase().includes(query),
    );
  }, [actualDashboardData?.children, searchQuery]);

  // Track changes in metrics and children for visual effects
  useEffect(() => {
    if (!dashboardData) return;

    const newMetrics = {
      totalBookings,
      inSessionCount,
      signedOutCount,
      atEcaCount,
      returningCount,
      notArrivedCount,
      absentCount,
    };

    const changed = new Set<string>();
    Object.entries(newMetrics).forEach(([key, value]) => {
      if (previousMetricsRef.current[key] !== undefined && previousMetricsRef.current[key] !== value) {
        changed.add(key);
      }
    });

    if (changed.size > 0) {
      setChangedMetrics(changed);
      setTimeout(() => setChangedMetrics(new Set()), 2000);
    }

    // Track changed children
    const changedIds = new Set<number>();
    if (previousMetricsRef.current.childrenData) {
      const prevChildren = previousMetricsRef.current.childrenData as SessionDashboardChild[];
      allChildren.forEach((child) => {
        const prevChild = prevChildren.find((c: SessionDashboardChild) => c.childId === child.childId);
        if (prevChild && prevChild.eventType !== child.eventType) {
          changedIds.add(child.childId);
        }
      });

      if (changedIds.size > 0) {
        setChangedChildIds(changedIds);
        setTimeout(() => setChangedChildIds(new Set()), 2000);
      }
    }

    // Track changed educators
    const allEducators = actualDashboardData?.educators || [];
    const changedEducatorIdSet = new Set<number>();
    if (previousMetricsRef.current.educatorsData) {
      const prevEducators = previousMetricsRef.current.educatorsData as SessionDashboardEducator[];

      // Check for added educators
      allEducators.forEach((educator) => {
        const prevEducator = prevEducators.find((e) => e.employeeId === educator.employeeId);
        if (!prevEducator) {
          changedEducatorIdSet.add(educator.employeeId);
        }
      });

      // Check for removed educators (mark as changed if they existed before)
      prevEducators.forEach((educator) => {
        const currentEducator = allEducators.find((e) => e.employeeId === educator.employeeId);
        if (!currentEducator) {
          changedEducatorIdSet.add(educator.employeeId);
        }
      });

      if (changedEducatorIdSet.size > 0) {
        setChangedEducatorIds(changedEducatorIdSet);
        setTimeout(() => setChangedEducatorIds(new Set()), 2000);
      }
    }

    previousMetricsRef.current = { ...newMetrics, childrenData: allChildren, educatorsData: allEducators };
  }, [
    totalBookings,
    inSessionCount,
    signedOutCount,
    atEcaCount,
    returningCount,
    notArrivedCount,
    absentCount,
    allChildren,
    dashboardData,
  ]);

  // Get children filtered by selected status
  const childrenByStatus = useMemo(() => {
    if (!selectedStatus) return [];

    if (selectedStatus === "bookings") {
      return filteredChildren;
    }

    return filteredChildren.filter((child) => getChildStatus(child.eventType) === selectedStatus);
  }, [filteredChildren, selectedStatus]);

  const handleTileClick = (status: ChildStatus) => {
    setSelectedStatus(status);
    setSearchQuery("");
  };

  const handleBackToTiles = () => {
    setSelectedStatus(null);
    setSearchQuery("");
  };

  if (!selectedService) {
    return (
      <div className={styles.emptyState}>
        <School className={styles.emptyStateIcon} />
        <p className={styles.emptyStateText}>Please select a service to view the dashboard</p>
      </div>
    );
  }

  if (sessionsError) {
    return <Alert severity="error">Error loading sessions: {sessionsError.message}</Alert>;
  }

  if (sessions.length === 0) {
    return (
      <div className={styles.emptyState}>
        <School className={styles.emptyStateIcon} />
        <p className={styles.emptyStateText}>No sessions found for the selected date range</p>
      </div>
    );
  }

  return (
    <div className={styles.sessionDashboard}>
      {/* Session Navigation Header - Mint Green Card */}
      <div className={styles.sessionHeader}>
        <div className={styles.sessionNavigation}>
          <button
            className={styles.navButton}
            onClick={selectPreviousSession}
            disabled={!hasPreviousSession}
            aria-label="Previous session"
          >
            <ChevronLeft />
          </button>
          <span className={styles.sessionTypeLabel}>{getSessionTypeLabel(selectedSessionType)}</span>
          <button
            className={styles.navButton}
            onClick={selectNextSession}
            disabled={!hasNextSession}
            aria-label="Next session"
          >
            <ChevronRight />
          </button>
        </div>
        <div className={styles.sessionInfo}>
          <h2 className={styles.sessionName}>{selectedSession?.rollName || "Session"}</h2>
          <div className={styles.sessionDetails}>
            <span className={styles.sessionDate}>{selectedSession?.rollDate?.format("ddd, D MMM YYYY")}</span>
            <div className={styles.sessionTimePill}>
              <Schedule />
              <span>{selectedSession && formatSessionTime(selectedSession.startTime, selectedSession.endTime)}</span>
            </div>
          </div>
        </div>
        <div className={styles.refreshInfo}>
          <span className={styles.lastUpdated}>Updated {dayjs(lastUpdated).format("h:mm A")}</span>
        </div>

        <Tooltip title="Download CSV">
          <IconButton onClick={handleDownloadCsv} color="inherit" disabled={isDownloading} sx={{ ml: "auto" }}>
            {isDownloading ? <CircularProgress size={24} /> : <FileDownload />}
          </IconButton>
        </Tooltip>
      </div>

      {dashboardError ? (
        <Alert severity="error">Error loading dashboard data: {dashboardError.message}</Alert>
      ) : selectedStatus ? (
        // Filtered children view
        <>
          <div className={styles.filteredViewHeader}>
            <button className={styles.backButton} onClick={handleBackToTiles}>
              <ArrowBack />
              <span>Back</span>
            </button>
            <h3 className={styles.filteredViewTitle}>
              {getStatusLabel(selectedStatus)} ({childrenByStatus.length})
            </h3>
          </div>

          {/* Search Bar */}
          <div className={styles.searchBar}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by student name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Children List */}
          <div className={styles.childrenList}>
            <ChildrenList children={childrenByStatus} changedChildIds={changedChildIds} />
          </div>
        </>
      ) : (
        // Tiles view (initial dashboard)
        <>
          {/* Metrics Grid */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box
              className={`${styles.metricCard} ${styles.metricCardBookings} ${changedMetrics.has("totalBookings") ? styles.metricCardChanged : ""}`}
              onClick={() => handleTileClick("bookings")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleTileClick("bookings")}
              sx={{ flex: { xs: "0 0 calc(50% - 8px)", md: "0 0 calc(50% - 8px)", lg: "0 0 calc(33.333% - 11px)" } }}
            >
              <span className={styles.metricLabel}>Bookings</span>
              <span className={styles.metricValue}>{totalBookings}</span>
            </Box>
            {inSessionCount > 0 && (
              <Box
                className={`${styles.metricCard} ${styles.metricCardInSession} ${changedMetrics.has("inSessionCount") ? styles.metricCardChanged : ""}`}
                onClick={() => handleTileClick("inSession")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleTileClick("inSession")}
                sx={{ flex: { xs: "0 0 calc(50% - 8px)", md: "0 0 calc(50% - 8px)", lg: "0 0 calc(33.333% - 11px)" } }}
              >
                <span className={styles.metricLabel}>In Session</span>
                <span className={styles.metricValue}>{inSessionCount}</span>
              </Box>
            )}
            {signedOutCount > 0 && (
              <Box
                className={`${styles.metricCard} ${styles.metricCardSignedOut} ${changedMetrics.has("signedOutCount") ? styles.metricCardChanged : ""}`}
                onClick={() => handleTileClick("signedOut")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleTileClick("signedOut")}
                sx={{ flex: { xs: "0 0 calc(50% - 8px)", md: "0 0 calc(50% - 8px)", lg: "0 0 calc(33.333% - 11px)" } }}
              >
                <span className={styles.metricLabel}>Signed Out</span>
                <span className={styles.metricValue}>{signedOutCount}</span>
              </Box>
            )}
            {atEcaCount > 0 && (
              <Box
                className={`${styles.metricCard} ${styles.metricCardAtEca} ${changedMetrics.has("atEcaCount") ? styles.metricCardChanged : ""}`}
                onClick={() => handleTileClick("atEca")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleTileClick("atEca")}
                sx={{ flex: { xs: "0 0 calc(50% - 8px)", md: "0 0 calc(50% - 8px)", lg: "0 0 calc(33.333% - 11px)" } }}
              >
                <span className={styles.metricLabel}>At ECA</span>
                <span className={styles.metricValue}>{atEcaCount}</span>
              </Box>
            )}
            {returningCount > 0 && (
              <Box
                className={`${styles.metricCard} ${styles.metricCardReturning} ${changedMetrics.has("returningCount") ? styles.metricCardChanged : ""}`}
                onClick={() => handleTileClick("returning")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleTileClick("returning")}
                sx={{ flex: { xs: "0 0 calc(50% - 8px)", md: "0 0 calc(50% - 8px)", lg: "0 0 calc(33.333% - 11px)" } }}
              >
                <span className={styles.metricLabel}>Returning</span>
                <span className={styles.metricValue}>{returningCount}</span>
              </Box>
            )}
            {notArrivedCount > 0 && (
              <Box
                className={`${styles.metricCard} ${styles.metricCardNotArrived} ${changedMetrics.has("notArrivedCount") ? styles.metricCardChanged : ""}`}
                onClick={() => handleTileClick("notArrived")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleTileClick("notArrived")}
                sx={{ flex: { xs: "0 0 calc(50% - 8px)", md: "0 0 calc(50% - 8px)", lg: "0 0 calc(33.333% - 11px)" } }}
              >
                <span className={styles.metricLabel}>To Be Signed In</span>
                <span className={styles.metricValue}>{notArrivedCount}</span>
              </Box>
            )}
            {absentCount > 0 && (
              <Box
                className={`${styles.metricCard} ${styles.metricCardAbsent} ${changedMetrics.has("absentCount") ? styles.metricCardChanged : ""}`}
                onClick={() => handleTileClick("absent")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleTileClick("absent")}
                sx={{ flex: { xs: "0 0 calc(50% - 8px)", md: "0 0 calc(50% - 8px)", lg: "0 0 calc(33.333% - 11px)" } }}
              >
                <span className={styles.metricLabel}>Absent</span>
                <span className={styles.metricValue}>{absentCount}</span>
              </Box>
            )}
          </Box>

          {/* Educators Section */}
          <div className={styles.educatorsSection}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                <span className={`${styles.sectionDot}`} style={{ backgroundColor: "#1d4e5c" }} />
                Educators
              </h3>
            </div>
            <div className={styles.educatorsList}>
              <EducatorsList educators={actualDashboardData?.educators || []} changedEducatorIds={changedEducatorIds} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface ChildrenListProps {
  children: SessionDashboardChild[];
  changedChildIds?: Set<number>;
}

const ChildrenList: React.FC<ChildrenListProps> = ({ children, changedChildIds = new Set() }) => {
  if (children.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateText}>No children in this category</p>
      </div>
    );
  }

  // Convert SessionDashboardChild to IChildCardData
  const toChildCardData = (child: SessionDashboardChild): IChildCardData => ({
    childId: child.childId,
    bookingId: child.bookingId,
    firstName: child.firstName,
    lastName: child.lastName,
    gender: child.gender,
    parentFirstName: child.parentFirstName,
    parentLastName: child.parentLastName,
    primaryServiceName: child.childPreferredSchoolName ?? child.childPreferredServiceName ?? child.primaryServiceName,
    eventType: child.eventType,
    eventTime: child.eventTime,
  });

  // Debug: Check for duplicate keys
  const keys = children.map((child) => `${child.childId}-${child.bookingId}`);
  const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
  if (duplicates.length > 0) {
    console.warn("Duplicate child keys detected in SessionDashboard:", [...new Set(duplicates)], children);
  }

  return (
    <>
      {children.map((child) => {
        const isChanged = changedChildIds.has(child.childId);
        return (
          <ChildCard
            key={`${child.childId}-${child.bookingId}`}
            child={toChildCardData(child)}
            showStatus={true}
            showGender={false}
            isChanged={isChanged}
          />
        );
      })}
    </>
  );
};

interface EducatorsListProps {
  educators: SessionDashboardEducator[];
  changedEducatorIds?: Set<number>;
}

const EducatorsList: React.FC<EducatorsListProps> = ({ educators, changedEducatorIds = new Set() }) => {
  const { leadership, support } = groupAndSortEducators(educators.map(toEmployeePosition));

  if (leadership.length === 0 && support.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateText}>No educators rostered for this session</p>
      </div>
    );
  }

  return (
    <>
      <EducatorSubSection title="LEADERSHIP" educators={leadership} changedEducatorIds={changedEducatorIds} />
      <EducatorSubSection title="EDUCATORS & SUPPORT" educators={support} changedEducatorIds={changedEducatorIds} />
    </>
  );
};

export default SessionDashboard;
